// MemberHome.jsx — Accueil espace membre (white-label, JAMAIS de mention AssoPerf)
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useOutletContext, Link } from 'react-router-dom';
import { Calendar, Ticket, ArrowRight, Loader2, Sparkles } from 'lucide-react';

export default function MemberHome() {
  const { user } = useAuth();
  const { asso } = useOutletContext();
  const [evts, setEvts] = useState([]);
  const [billets, setBillets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          api.get('/evenements'),
          api.get('/billets/mes-billets'),
        ]);
        setEvts(a.data); setBillets(b.data);
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="p-8 grid place-items-center min-h-[60vh]">
        <Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" />
      </div>
    );
  }

  const upcoming = evts.filter((e) => new Date(e.date_debut) > new Date()).slice(0, 3);
  const activeBillets = billets.filter((b) => b.statut === 'valide').length;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="member-home">

      {/* En-tête éditorial */}
      <div className="mb-10 slide-up">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--theme-primary)' }}>
          Bonjour {user?.prenom}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#0F172A] leading-[1.02]">
          Bienvenue dans <span className="italic font-medium" style={{ color: 'var(--theme-primary)' }}>votre espace.</span>
        </h1>
      </div>

      {/* Hero card — branding asso */}
      <div
        className="relative rounded-3xl p-8 sm:p-10 mb-10 text-white overflow-hidden"
        style={{ background: 'var(--theme-primary)', color: 'var(--theme-primary-foreground)' }}
        data-testid="member-hero"
      >
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-white/10" aria-hidden />
        <div className="absolute right-32 bottom-0 w-32 h-32 rounded-full bg-white/5" aria-hidden />
        <div className="relative max-w-lg">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] font-bold uppercase tracking-[0.2em] mb-5">
            <Sparkles className="w-3 h-3" />
            Votre asso
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-3">
            {asso.nom}
          </h2>
          <p className="text-sm sm:text-base text-white/85 leading-relaxed">
            Retrouvez ici tous vos événements à venir, vos billets et votre profil.
          </p>
        </div>
      </div>

      {/* Stats compactes */}
      <div className="grid grid-cols-2 gap-4 mb-12">
        <StatBlock icon={Calendar} value={upcoming.length} label="Événements à venir" />
        <StatBlock icon={Ticket} value={activeBillets} label="Billets actifs" />
      </div>

      {/* Prochains événements */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl font-bold text-[#0F172A] leading-tight">Prochains événements</h2>
          <Link
            to="/espace/evenements"
            className="text-xs font-bold text-[#475569] hover:text-[#0F172A] flex items-center gap-1 transition-colors"
          >
            Tout voir <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        {upcoming.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-3xl p-12 text-center">
            <div className="w-12 h-12 rounded-2xl bg-[#F1F5F9] grid place-items-center mx-auto mb-3">
              <Calendar className="w-5 h-5 text-[#94A3B8]" />
            </div>
            <p className="text-sm text-[#475569]">Aucun événement à venir.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="member-upcoming-list">
            {upcoming.map((e) => {
              const date = new Date(e.date_debut);
              return (
                <Link
                  key={e.id}
                  to="/espace/evenements"
                  className="group bg-white border border-[#E2E8F0] rounded-2xl p-4 flex items-center gap-4 transition-all hover:border-[#94A3B8] hover:-translate-y-0.5"
                >
                  <div className="w-14 h-14 rounded-2xl grid place-items-center shrink-0" style={{ background: 'var(--theme-primary-soft)', color: 'var(--theme-primary)' }}>
                    <div className="text-center leading-none">
                      <div className="text-[9px] uppercase tracking-wider font-bold">{date.toLocaleDateString('fr-FR', { month: 'short' })}</div>
                      <div className="font-display text-xl font-black mt-0.5">{date.getDate()}</div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#0F172A] truncate">{e.nom}</div>
                    <div className="text-xs text-[#475569] mt-0.5">
                      {date.toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      {e.lieu && ` · ${e.lieu}`}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#94A3B8] transition-transform group-hover:translate-x-1" />
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StatBlock({ icon: Icon, value, label }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-3xl p-5 sm:p-6 transition-all hover:shadow-md hover:-translate-y-0.5">
      <div className="w-9 h-9 rounded-lg grid place-items-center mb-4" style={{ background: 'var(--theme-primary-soft)', color: 'var(--theme-primary)' }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">{value}</div>
      <div className="text-xs text-[#475569] mt-1">{label}</div>
    </div>
  );
}
